import { useCallback, useEffect, useState, useRef } from 'react';
import { apiClient } from '../../../auth-lib';
import { Card, Skeleton } from '../../../design-system';
import '../../styles/Reports.css';

// ── Formatters (Indian conventions) ──────────────────────────────────────────
export const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
export const fmtMoneyShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
  return `₹${v}`;
};
export const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');
export const fmtPct = (n) => (n == null ? '—' : `${n}%`);
export const fmtHours = (n) => `${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}h`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const monthLabel = (key) => {
  const [y, m] = String(key).split('-');
  return `${MON[Number(m) - 1] || ''} '${(y || '').slice(2)}`;
};

// Tone → CSS var (works in both themes; also usable as SVG fill).
export const toneColor = (tone) =>
  ({
    accent: 'var(--ds-accent-600)',
    blue: 'var(--ds-info-600)',
    green: 'var(--ds-success-600)',
    amber: 'var(--ds-warning-600)',
    red: 'var(--ds-danger-600)',
    slate: 'var(--ds-neutral-400)',
  }[tone] || 'var(--ds-accent-600)');

export const pctTone = (p) => (p == null ? 'slate' : p >= 90 ? 'green' : p >= 70 ? 'amber' : 'red');

// ── Data hook ────────────────────────────────────────────────────────────────
export function useReport(path, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const key = JSON.stringify(params || {});
  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`/api/reports/${path}`, { params: params || {} })
      .then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key]);
  useEffect(load, [load]);
  return { data, loading, error, reload: load };
}

// ── Card + section header ────────────────────────────────────────────────────
export function ReportCard({ title, subtitle, icon, action, loading, error, children }) {
  return (
    <Card>
      <div className="rep-head">
        <div className="rep-head-main">
          {icon && <span className="rep-head-ico" aria-hidden>{icon}</span>}
          <div>
            <h3 className="rep-head-title">{title}</h3>
            {subtitle && <p className="rep-head-sub">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {loading ? <Skeleton.Text lines={4} /> : error ? <p className="rep-error">{error}</p> : children}
    </Card>
  );
}

// ── KPI stat cards ───────────────────────────────────────────────────────────
export function StatRow({ children }) { return <div className="rep-stat-row">{children}</div>; }
export function Stat({ label, value, tone = 'slate', hint }) {
  return (
    <div className={`rep-stat rep-tone-bd-${tone}`}>
      <span className="rep-stat-label">{label}</span>
      <span className={`rep-stat-value rep-tone-${tone}`}>{value}</span>
      {hint && <span className="rep-stat-hint">{hint}</span>}
    </div>
  );
}

// ── Legend ───────────────────────────────────────────────────────────────────
export function Legend({ items }) {
  return (
    <div className="rep-legend">
      {items.map((it) => (
        <span className="rep-legend-item" key={it.label}>
          <span className="rep-legend-swatch" style={{ background: it.color }} />
          {it.label}{it.value != null && <b className="rep-legend-val">{it.value}</b>}
        </span>
      ))}
    </div>
  );
}

// ── Donut / gauge (SVG) ──────────────────────────────────────────────────────
// segments: [{label, value, color}]. Renders arcs with a 2px surface gap.
export function Donut({ segments, size = 148, thickness = 18, centerValue, centerLabel }) {
  const data = (segments || []).filter((s) => (s.value || 0) > 0);
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const gap = total > 0 && data.length > 1 ? 2 : 0; // px gap between segments
  let offset = 0;
  return (
    <div className="rep-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--ds-neutral-150)" strokeWidth={thickness} />
        {total > 0 &&
          data.map((d, i) => {
            const frac = d.value / total;
            const len = Math.max(0, frac * c - gap);
            const dash = `${len} ${c - len}`;
            const el = (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={d.color}
                strokeWidth={thickness}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              >
                <title>{`${d.label}: ${d.value}`}</title>
              </circle>
            );
            offset += frac * c;
            return el;
          })}
        <text x="50%" y="47%" className="rep-donut-num" textAnchor="middle" dominantBaseline="middle">{centerValue}</text>
        <text x="50%" y="62%" className="rep-donut-cap" textAnchor="middle" dominantBaseline="middle">{centerLabel}</text>
      </svg>
    </div>
  );
}

// Single-value ring gauge (percentage). Color by threshold.
export function Gauge({ value, label, size = 148 }) {
  const v = Math.max(0, Math.min(100, value == null ? 0 : value));
  const color = toneColor(pctTone(value));
  return (
    <Donut
      size={size}
      thickness={16}
      segments={[{ label, value: v, color }, { label: 'remaining', value: 100 - v, color: 'transparent' }]}
      centerValue={value == null ? '—' : `${value}%`}
      centerLabel={label}
    />
  );
}

// ── Horizontal magnitude bars ────────────────────────────────────────────────
export function BarList({ items, valueFmt = fmtNum, color = 'var(--ds-accent-600)', emptyText = 'No data', max: maxProp }) {
  if (!items || items.length === 0) return <p className="rep-empty">{emptyText}</p>;
  const max = maxProp || Math.max(1, ...items.map((i) => Math.abs(i.value || 0)));
  return (
    <div className="rep-barlist">
      {items.map((it, idx) => (
        <div className="rep-bar-row" key={it.label + idx} title={`${it.label}: ${valueFmt(it.value)}`}>
          <span className="rep-bar-label">{it.label}</span>
          <div className="rep-bar-track">
            <div className="rep-bar-fill" style={{ width: `${(Math.abs(it.value || 0) / max) * 100}%`, background: it.color || color }} />
          </div>
          <span className="rep-bar-value">{valueFmt(it.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Diverging bars around zero (variance: over = red, under = green).
export function DivergingBars({ items, valueFmt = fmtNum, emptyText = 'No data' }) {
  if (!items || items.length === 0) return <p className="rep-empty">{emptyText}</p>;
  const max = Math.max(1, ...items.map((i) => Math.abs(i.value || 0)));
  return (
    <div className="rep-barlist">
      {items.map((it, idx) => {
        const over = (it.value || 0) > 0;
        const w = (Math.abs(it.value || 0) / max) * 50;
        return (
          <div className="rep-bar-row rep-diverge" key={it.label + idx} title={`${it.label}: ${valueFmt(it.value)}`}>
            <span className="rep-bar-label">{it.label}</span>
            <div className="rep-diverge-track">
              <span className="rep-diverge-mid" />
              <div
                className="rep-bar-fill"
                style={{
                  width: `${w}%`,
                  marginLeft: over ? '50%' : `${50 - w}%`,
                  background: over ? 'var(--ds-danger-600)' : 'var(--ds-success-600)',
                }}
              />
            </div>
            <span className={`rep-bar-value rep-tone-${over ? 'red' : 'green'}`}>{over ? '+' : ''}{valueFmt(it.value)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Time-series: stacked bars by month (with hover tooltip) ──────────────────
// data: [{key, ...seriesValues}]; series: [{key,label,color}]
export function StackedBars({ data, series, valueFmt = fmtNum, height = 150, emptyText = 'No data' }) {
  const [hover, setHover] = useState(null);
  if (!data || data.length === 0) return <p className="rep-empty">{emptyText}</p>;
  const totals = data.map((d) => series.reduce((s, se) => s + (d[se.key] || 0), 0));
  const max = Math.max(1, ...totals);
  return (
    <div className="rep-chart">
      <div className="rep-bars" style={{ height }}>
        {data.map((d, i) => {
          const total = totals[i];
          return (
            <div
              className="rep-bars-col"
              key={d.key}
              onMouseEnter={() => setHover({ i, d, total })}
              onMouseLeave={() => setHover((h) => (h && h.i === i ? null : h))}
            >
              <div className="rep-bars-stack">
                {series.map((se) => {
                  const v = d[se.key] || 0;
                  if (!v) return null;
                  return <div key={se.key} className="rep-bars-seg" style={{ height: `${(v / max) * 100}%`, background: se.color }} />;
                })}
              </div>
              <span className="rep-bars-x">{monthLabel(d.key).split(' ')[0]}</span>
              {hover?.i === i && (
                <div className="rep-tip">
                  <div className="rep-tip-title">{monthLabel(d.key)}</div>
                  {series.map((se) => (
                    <div className="rep-tip-row" key={se.key}>
                      <span className="rep-legend-swatch" style={{ background: se.color }} /> {se.label}
                      <b>{valueFmt(d[se.key] || 0)}</b>
                    </div>
                  ))}
                  {series.length > 1 && <div className="rep-tip-row rep-tip-total">Total <b>{valueFmt(total)}</b></div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {series.length > 1 && <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} />}
    </div>
  );
}

// ── Time-series: grouped bars by month (side by side, shared scale) ─────────
// Same data contract as StackedBars, but the series sit BESIDE each other
// rather than on top. Use this whenever stacking would be a lie — billed and
// collected in a month are two measurements of the same money, so a stack of
// them adds up to a number that means nothing.
export function GroupedBars({ data, series, valueFmt = fmtNum, height = 160, emptyText = 'No data' }) {
  const [hover, setHover] = useState(null);
  if (!data || data.length === 0) return <p className="rep-empty">{emptyText}</p>;
  const max = Math.max(1, ...data.flatMap((d) => series.map((se) => d[se.key] || 0)));
  return (
    <div className="rep-chart">
      <div className="rep-bars" style={{ height }}>
        {data.map((d, i) => (
          <div
            className="rep-bars-col"
            key={d.key}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover((h) => (h === i ? null : h))}
          >
            <div className="rep-bars-group">
              {series.map((se) => (
                <div
                  key={se.key}
                  className="rep-bars-gseg"
                  style={{ height: `${((d[se.key] || 0) / max) * 100}%`, background: se.color }}
                />
              ))}
            </div>
            <span className="rep-bars-x">{monthLabel(d.key).split(' ')[0]}</span>
            {hover === i && (
              <div className="rep-tip">
                <div className="rep-tip-title">{monthLabel(d.key)}</div>
                {series.map((se) => (
                  <div className="rep-tip-row" key={se.key}>
                    <span className="rep-legend-swatch" style={{ background: se.color }} /> {se.label}
                    <b>{valueFmt(d[se.key] || 0)}</b>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <Legend items={series.map((s) => ({ label: s.label, color: s.color }))} />
    </div>
  );
}

// ── Time-series: single-series area (gradient fill + line + hover) ───────────
export function AreaTrend({ data, valueFmt = fmtNum, color = 'var(--ds-accent-600)', height = 150, emptyText = 'No data' }) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  if (!data || data.length === 0) return <p className="rep-empty">{emptyText}</p>;
  const W = 640, H = 160, pad = 6;
  const n = data.length;
  const max = Math.max(1, ...data.map((d) => d.value || 0));
  const x = (i) => (n === 1 ? W / 2 : pad + (i / (n - 1)) * (W - 2 * pad));
  const y = (v) => H - pad - ((v || 0) / max) * (H - 2 * pad);
  const pts = data.map((d, i) => [x(i), y(d.value)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${pts[n - 1][0].toFixed(1)},${H - pad} L${pts[0][0].toFixed(1)},${H - pad} Z`;
  const gid = `rep-grad-${Math.random().toString(36).slice(2, 7)}`;

  const onMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rel = (e.clientX - rect.left) / rect.width;
    const i = Math.max(0, Math.min(n - 1, Math.round(rel * (n - 1))));
    setHover(i);
  };

  return (
    <div className="rep-chart">
      <div className="rep-area-wrap" ref={wrapRef} style={{ height }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="rep-area-svg" width="100%" height={height}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gid})`} />
          <path d={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
          {hover != null && (
            <>
              <line x1={pts[hover][0]} y1={pad} x2={pts[hover][0]} y2={H - pad} stroke="var(--ds-neutral-300)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <circle cx={pts[hover][0]} cy={pts[hover][1]} r="4" fill={color} stroke="var(--ds-bg-surface)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
            </>
          )}
        </svg>
        {hover != null && (
          <div className="rep-tip rep-tip-float" style={{ left: `${(x(hover) / W) * 100}%` }}>
            <div className="rep-tip-title">{monthLabel(data[hover].key)}</div>
            <div className="rep-tip-row"><span className="rep-legend-swatch" style={{ background: color }} /><b>{valueFmt(data[hover].value)}</b></div>
          </div>
        )}
      </div>
      <div className="rep-xaxis">
        {data.map((d, i) => (
          <span key={d.key} className="rep-xtick" style={{ visibility: n > 8 && i % 2 ? 'hidden' : 'visible' }}>
            {monthLabel(d.key).split(' ')[0]}
          </span>
        ))}
      </div>
    </div>
  );
}

// Count pills (status breakdown, always labeled).
export function CountPills({ counts, tones = {} }) {
  const entries = Object.entries(counts || {}).filter(([, v]) => v != null);
  if (entries.length === 0) return <p className="rep-empty">No data</p>;
  return (
    <div className="rep-pills">
      {entries.map(([k, v]) => (
        <span className={`rep-pill rep-pill-${tones[k] || 'slate'}`} key={k}>
          <b>{fmtNum(v)}</b> {k}
        </span>
      ))}
    </div>
  );
}
